import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  keyframes
} from '@mui/material';
import {
  CheckCircle,
  Add,
  Remove,
  ShoppingCart,
  TuneRounded
} from '@mui/icons-material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';

function Pricing() {
  const pulseGlow = keyframes`
    0% {
      box-shadow: 0 0 0 0 rgba(30, 58, 138, 0.4);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(30, 58, 138, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(30, 58, 138, 0);
    }
  `;

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, setUser } = useAuth();
  const [loading, setLoading] = useState({});
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [plans, setPlans] = useState([]);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [limitPopupOpen, setLimitPopupOpen] = useState(false);
  const [limitPopupMessage, setLimitPopupMessage] = useState('');
  const [gstPreviews, setGstPreviews] = useState({});
  const [stateRequiredOpen, setStateRequiredOpen] = useState(false);

  const handleQuantityChange = (val) => {
    const newVal = Math.max(1, Math.min(100, val));
    setQuantity(newVal);
  };

  React.useEffect(() => {
    fetchPlans();
  }, []);

  // GST breakdown is always computed server-side — fetch a display-only preview
  // per paid plan once the recruiter is authenticated (never computed client-side).
  React.useEffect(() => {
    if (!isAuthenticated || plans.length === 0) return;
    const paidPlans = plans.filter(p => p.planCode !== 'free' && p.planCode !== 'custom' && p.planCode !== 'pay_as_you_go');
    paidPlans.forEach(async (p) => {
      try {
        const res = await api.get('/payment/gst-preview', { params: { planCode: p.planCode.toUpperCase() } });
        setGstPreviews(prev => ({ ...prev, [p.planCode]: res.data }));
      } catch (err) {
        console.error(`Failed to fetch GST preview for ${p.planCode}:`, err);
      }
    });
  }, [isAuthenticated, plans]);

  const formatRupee = (paise) => `₹${(Number(paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fetchPlans = async () => {
    try {
      setFetchingPlans(true);
      const response = await api.get('/payment/plans');
      const backendPlans = response.data.plans || [];
      
      // Filter for recruiter/admin plans only (exclude candidate plans for this page)
      // Also filter out CUSTOM if it comes from backend to avoid duplication, as we add it manually below
      const recruiterPlans = backendPlans.filter(p => 
        !p.code.startsWith('CANDIDATE_') && 
        p.code !== 'PAY_AS_YOU_GO' &&
        p.code !== 'CUSTOM'
      );

      // Transform backend plans to UI format
      const mappedPlans = recruiterPlans.map(p => {
        let period = '';
        if (p.code === 'MONTHLY') period = '/ 3 Months';
        else if (p.code === 'ANNUAL') period = '/ Year';
        else if (p.code === 'FREE') period = 'Lifetime';
        else if (p.code === 'PAY_AS_YOU_GO') period = 'per Job';

        return {
          title: p.name,
          price: p.amount === 0 ? '₹0' : `₹${(p.amount / 100).toLocaleString()}`,
          priceAmount: p.amount / 100,
          period: period,
          planCode: p.code.toLowerCase(),
          description: p.description,
          features: p.features || [],
          buttonText: p.code === 'FREE' ? 'Try Now' : 'Get Started',
          variant: p.code === 'ANNUAL' ? 'contained' : 'outlined',
          popular: p.code === 'ANNUAL'
        };
      });

      // Add Custom Plan
      mappedPlans.push({
        title: 'Custom Plan',
        price: "Let's Build",
        period: 'for You',
        planCode: 'custom',
        description: "Need more flexibility? We'll create a plan that fits your hiring needs.",
        features: [
          'Custom Active Jobs',
          'Custom Applications',
          'Advanced Features',
           'Custom WhatsApp Alerts',
            'Custom AI Voice Calling',
          'Priority Support',
          'Tailored Integrations'
        ],
        buttonText: 'Contact Sales',
        variant: 'outlined',
        popular: false
      });

      setPlans(mappedPlans);
    } catch (err) {
      console.error('Failed to fetch pricing plans:', err);
      setError('Failed to load pricing plans. Please try again later.');
    } finally {
      setFetchingPlans(false);
    }
  };

  async function handleSubscribe(planCode, qty = 1){
    if (planCode === 'custom') {
      navigate('/contact');
      return;
    }
    if (!isAuthenticated) {
      setError('Please login first to subscribe.');
      navigate('/login');
      return;
    }

    if (planCode === 'free') {
      if (user?.has_used_free_trial) {
        setError('You have already used your one-time free trial.');
        return;
      }
      navigate('/free-trial-confirm');
      return;
    }

    setLoading(prev => ({ ...prev, [planCode]: true }));
    setError('');

    try {
      const orderRes = await api.post('/payment/create-order', {
        planCode: planCode.toUpperCase(),
        quantity: qty
      });
      const { orderId, amount, currency, key } = orderRes.data;

      if (!orderId || !key) {
        throw new Error('Invalid response from payment server. Please try again later.');
      }

      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: 'ExiHire',
        description:
          planCode === 'pay_as_you_go'
            ? `${quantity} Job Credit(s) - Pay-As-You-Go`
            : `${planCode.charAt(0).toUpperCase() + planCode.slice(1)} Subscription`,
        order_id: orderId,
        handler: async function (response) {
          try {
            const verifyRes = await api.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planCode: planCode.toUpperCase(),
              quantity: qty,
              purchaseType: 'subscription'
            });

            if (verifyRes.data.success) {
              try {
                const userRes = await api.get('/auth/user');
                setUser(userRes.data);
              } catch (e) {
                console.error("Failed to refresh user", e);
              }

              const fromAdminRequest = localStorage.getItem('fromAdminRequest');

              if (fromAdminRequest === 'true') {
                localStorage.removeItem('fromAdminRequest');

                setSuccessMessage(
                  'Form successfully submitted and payment received. Your request is pending super admin approval.'
                );

                setSuccessOpen(true);

                setTimeout(() => {
                  navigate('/candidate/dashboard');
                }, 2000);

              } else {

                setSuccessMessage('Subscription successful!');

                setSuccessOpen(true);

                setTimeout(() => {
                  navigate(
                    user?.role === 'admin' || user?.role === 'recruiter'
                      ? '/admin/dashboard'
                      : '/candidate/dashboard'
                  );
                }, 2000);
              }
            }
          } catch (err) {
            console.error('Payment verification failed:', err);
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: `${user?.firstName || ''} ${user?.lastName || ''}`,
          email: user?.email || '',
        },
        theme: {
          color: '#1E3A8A',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Failed to initiate subscription:', err);

      if (err.response?.data?.code === 'STATE_REQUIRED') {
        setStateRequiredOpen(true);
      } else if (!err.response) {
        // Better message for CORS / API Gateway mapping failures (no response)
        setError('Unable to connect to the payment service. Please try again in a moment.');
      } else {
        setError(
          err.response?.data?.message ||
            'Failed to initiate subscription. Please try again.'
        );
      }
    } finally {
      setLoading(prev => ({ ...prev, [planCode]: false }));
    }
    }
  const queryParams = new URLSearchParams(location.search);
  const planCode = queryParams.get('plan');
  const isProfessionalUpgrade = planCode === "professional";

  return (
    <Container maxWidth={false} sx={{ mt: 0, py: { xs: 4, md: 8 }, minHeight: '100vh', maxWidth: { xs: '100%', lg: '1200px', xxl: '1440px', xxxl: '1800px' } }}>
      {isProfessionalUpgrade ? (
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
            borderRadius: '999px', bgcolor: '#EFF6FF', border: '1px solid #e9d5ff', mb: 2,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1e3a8a' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Professional Plan
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
            Unlock Professional Features
          </Typography>
          <Typography sx={{ color: '#6b7280', maxWidth: 480, mx: 'auto', fontSize: '0.95rem', lineHeight: 1.7 }}>
            Get full access to Candidate Comparison, Advanced Analytics, Export Reports, and AI-powered hiring insights.
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontWeight: "bold",
            color: "#0A0F2C",
            mb: 4,
            fontSize: { xs: "2rem", md: "3rem" },
          }}
          gutterBottom
        >
          Subscription Plans
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
      {fetchingPlans ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid container spacing={4} alignItems="stretch" justifyContent="center">
          {plans
            .filter((plan) => {
              if (isProfessionalUpgrade) {
                return plan.planCode === "annual";
              }
              return true;
            })
            .map((plan, index) => (
            <Grid item key={index} xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',

                  border:
                    plan.planCode === "custom"
                      ? "1px solid #E7D7FF"
                      : plan.popular
                      ? "2px solid #1E3A8A"
                      : "none",

                  background:
                    plan.planCode === "custom"
                      ? "linear-gradient(180deg, #FCF8FF 0%, #F5EDFF 100%)"
                      : "#FFFFFF",

                  boxShadow:
                    plan.planCode === "custom"
                      ? "0 8px 24px rgba(168,85,247,0.06)"
                      : plan.popular
                      ? 6
                      : 1,

                  transform: {
                    md: plan.popular ? "scale(1.05)" : "none",
                    xs: "none",
                  },

                  mt: {
                    xs: plan.popular ? 2 : 0,
                    md: 0,
                  },

                  zIndex: plan.popular ? 1 : 0,

                  width: "100%",

                  borderRadius: "24px",
                }}
              >
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 9,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: '#1E3A8A',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {plan.planCode === 'annual' ? 'RECOMMENDED' : 'MOST POPULAR'}
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                {plan.planCode === "custom" ? (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: "50%",
                         background: "#FFFFFF",
                         border: "2px solid #E9D5FF",
                         boxShadow: "0 6px 18px rgba(168,85,247,0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <TuneRounded
                          sx={{
                            fontSize: 44,
                             color:"#6366F1"
                          }}
                        />
                      </Box>
                    </Box>

                    <Typography
                      align="center"
                      sx={{
                        fontSize: "2rem",
                        fontWeight: 700,
                        color: "#111827",
                        mb: 1,
                      }}
                    >
                      Custom Plan
                    </Typography>

                    <Typography
                      align="center"
                      sx={{
                        fontSize: "1.65rem",
                        fontWeight: 700,
                        lineHeight: 1.15,
                      }}
                    >
                      Let's Build
                    </Typography>

                    <Typography
                      align="center"
                      sx={{
                        fontSize: "1.65rem",
                        fontWeight: 700,
                        lineHeight: 1.15,
                        mb: 3,
                      }}
                    >
                      for You
                    </Typography>

                    <Typography
                      align="center"
                      sx={{
                        color:"#64748B",
                        fontSize: 15,
                        lineHeight: 1.7,
                        mb: 4,
                      }}
                    >
                      Need more flexibility?
                      <br />
                      We'll create a plan that fits
                      <br />
                      your hiring needs.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h5" component="h2" align="center" gutterBottom fontWeight="bold">
                      {plan.title}
                    </Typography>

                    {plan.planCode === 'annual' ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          mb: 2
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            textDecoration: 'line-through',
                            color: 'text.secondary'
                          }}
                        >
                          ₹99,999/year
                        </Typography>

                        <Typography variant="h2" fontWeight="bold">
                          ₹49,999/year
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#1E3A8A',
                            fontWeight: 700,
                            mt: 0.5,
                            letterSpacing: '0.3px'
                          }}
                        >
                          First 50 Companies
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 2 }}>
                        <Typography variant="h3" component="span" fontWeight="bold">
                          {plan.planCode === 'pay_as_you_go'
                            ? `₹${(plan.priceAmount * quantity).toLocaleString()}`
                            : plan.price}
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          component="span"
                          sx={{ ml: 1, color: 'text.secondary' }}
                        >
                          {plan.planCode === 'pay_as_you_go'
                            ? (quantity > 1 ? `for ${quantity} jobs` : plan.period)
                            : plan.period}
                        </Typography>
                      </Box>
                    )}
                    {(plan.planCode === 'monthly' || plan.planCode === 'annual') && (
                      <Box sx={{ mb: 2, px: 1 }}>
                        {gstPreviews[plan.planCode]?.stateMissing ? (
                          <Typography variant="caption" align="center" display="block" sx={{ color: '#b45309' }}>
                            Prices are exclusive of GST. Applicable GST will be added based on your registered company state — please update it in your profile.
                          </Typography>
                        ) : gstPreviews[plan.planCode] ? (
                          <Box sx={{ borderTop: '1px dashed #e2e8f0', pt: 1.5, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748B' }}>
                              <span>Base Price</span>
                              <span>{formatRupee(gstPreviews[plan.planCode].baseAmount)}</span>
                            </Box>
                            {gstPreviews[plan.planCode].taxType === 'CGST_SGST' ? (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748B' }}>
                                  <span>CGST ({gstPreviews[plan.planCode].cgstRate}%)</span>
                                  <span>{formatRupee(gstPreviews[plan.planCode].cgstAmount)}</span>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748B' }}>
                                  <span>SGST ({gstPreviews[plan.planCode].sgstRate}%)</span>
                                  <span>{formatRupee(gstPreviews[plan.planCode].sgstAmount)}</span>
                                </Box>
                              </>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748B' }}>
                                <span>IGST ({gstPreviews[plan.planCode].igstRate}%)</span>
                                <span>{formatRupee(gstPreviews[plan.planCode].igstAmount)}</span>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 700, color: '#0A0F2C', mt: 0.5, pt: 0.5, borderTop: '1px solid #e2e8f0' }}>
                              <span>Total Payable</span>
                              <span>{formatRupee(gstPreviews[plan.planCode].totalPayable)}</span>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="caption" align="center" display="block" color="text.secondary">
                            Prices are exclusive of GST. Applicable GST will be added based on the registered company state.
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
                      {plan.description}
                    </Typography>
                  </>
                )}
                {plan.planCode === 'annual' && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      mt: -1,
                      py: '10px',
                      px: '20px',
                      borderRadius: '12px',
                      background: 'linear-gradient(90deg, #1E3A8A, #60A5FA)',
                      color: 'white',
                      boxShadow: '0 4px 15px rgba(30, 58, 138, 0.3)',
                      width: 'fit-content',
                      maxWidth: '100%',
                      mx: 'auto',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        textAlign: 'center'
                      }}
                    >
                      Launch Offer
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        textAlign: 'center'
                      }}
                    >
                      Exclusive Pricing for First 50 Companies
                    </Typography>
                  </Box>
                )}
                {plan.planCode === 'pay_as_you_go' && (
                  <Box 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(0, 139, 139, 0.05)',
                      border: '1px solid rgba(0, 139, 139, 0.1)',
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center' 
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mb: 1.5, 
                        fontWeight: 'bold', 
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        color: 'text.secondary'
                      }}
                    >
                      Number of Jobs
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' }}>
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => handleQuantityChange(quantity - 1)}
                        sx={{ 
                          minWidth: 32, 
                          width: 32, 
                          height: 32, 
                          p: 0, 
                          borderRadius: '50%',
                          bgcolor: '#1E3A8A',
                          '&:hover': { bgcolor: '#1E40AF' }
                        }}
                      >
                        <Remove fontSize="small" />
                      </Button>
                      <TextField
                        value={quantity}
                        onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                        size="small"
                        variant="standard"
                        InputProps={{ 
                          disableUnderline: true,
                        }}
                        sx={{ 
                          width: 60, 
                          '& .MuiInputBase-input': { 
                            textAlign: 'center', 
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            color: '#1E3A8A',
                            py: 0
                          } 
                        }}
                        inputProps={{ min: 1, max: 100 }}
                      />
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => handleQuantityChange(quantity + 1)}
                        sx={{ 
                          minWidth: 32, 
                          width: 32, 
                          height: 32, 
                          p: 0, 
                          borderRadius: '50%',
                          bgcolor: '#1E3A8A',
                          '&:hover': { bgcolor: '#1E40AF' }
                        }}
                      >
                        <Add fontSize="small" />
                      </Button>
                    </Box>
                  </Box>
                )}

                <List
                  sx={{
                    mb: 4,
                    ...(plan.planCode === "custom" && {
                      maxWidth: 240,
                      mx: "auto",
                    }),
                  }}
                >
                  {plan.features.map((feature, idx) => (
                    <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle sx={{ color: '#1E3A8A', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {plan.example && (
                  <Typography variant="caption" display="block" align="center" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                    {plan.example}
                  </Typography>
                )}
              </CardContent>
              <Box sx={{ p: 3, mt: 'auto' }}>
                <Button
                  fullWidth
                  variant={plan.variant}
                  size="large"
                  color="primary"
                  disabled={loading[plan.planCode]}
                  onClick={() => {
                    if (plan.planCode === 'free') {
                      setError('');

                      if (user?.hasUsedFreeTrial) {
                        setError("You have already used your free trial. Upgrade your plan.");
                        return;
                      }

                      navigate('/free-trial-confirm');
                    } else {
                            // FIX: Pass the 'quantity' state variable for Pay-As-You-Go
                            const selectedQty = plan.planCode === 'pay_as_you_go' ? quantity : 1;
                            handleSubscribe(plan.planCode, selectedQty);
                          }
                  }}
                  startIcon={plan.planCode !== 'free' ? <ShoppingCart /> : null}
                  sx={{
                    py: 1.5,
                    fontWeight: 'bold',
                    borderRadius: 2,
                    boxShadow: plan.variant === 'contained' ? '0 4px 14px 0 rgba(30,58,138,0.39)' : 'none',
                    '&:hover': {
                      boxShadow: plan.variant === 'contained' ? '0 6px 20px rgba(30,58,138,0.23)' : 'none',
                    }
                  }}
                >
                  {loading[plan.planCode] ? (
                    <CircularProgress size={24} />
                  ) : (
                    plan.buttonText
                  )}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
        </Grid>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', textAlign: 'center' }}>
          By continuing, you agree to our{' '}
          <Typography
            component={RouterLink}
            to="/terms-and-conditions"
            sx={{ color: '#1E3A8A', fontWeight: 600, fontSize: 'inherit', textDecoration: 'none', display: 'inline', '&:hover': { textDecoration: 'underline' } }}
          >
            Terms & Conditions
          </Typography>{' '}
          and{' '}
          <Typography
            component={RouterLink}
            to="/privacy-policy"
            sx={{ color: '#1E3A8A', fontWeight: 600, fontSize: 'inherit', textDecoration: 'none', display: 'inline', '&:hover': { textDecoration: 'underline' } }}
          >
            Privacy Policy
          </Typography>
          .
        </Typography>
      </Box>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#1E3A8A',
            pt: 4
          }}
        >
          Payment Successful
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              textAlign: 'center',
              py: 2,
              fontSize: '16px'
            }}
          >
            {successMessage}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 4
          }}
        >
          <Button
            variant="contained"
            onClick={() => setSuccessOpen(false)}
            sx={{
              backgroundColor: '#1E3A8A',
              px: 5,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#006d6d'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={limitPopupOpen}
        onClose={() => setLimitPopupOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#d32f2f',
            pt: 4
          }}
        >
          Purchase Limit
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              textAlign: 'center',
              py: 2,
              fontSize: '16px'
            }}
          >
            {limitPopupMessage}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 4
          }}
        >
          <Button
            variant="contained"
            onClick={() => setLimitPopupOpen(false)}
            sx={{
              backgroundColor: '#1E3A8A',
              px: 5,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#006d6d'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stateRequiredOpen}
        onClose={() => setStateRequiredOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#1E3A8A',
            pt: 4
          }}
        >
          State Required
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              textAlign: 'center',
              py: 2,
              fontSize: '16px'
            }}
          >
            Please update your registered State in your profile before subscribing to a paid plan. GST is calculated based on this state.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 4,
            gap: 1
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setStateRequiredOpen(false)}
            sx={{ borderColor: '#1E3A8A', color: '#1E3A8A', px: 3, borderRadius: 2, fontWeight: 'bold' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { setStateRequiredOpen(false); navigate('/profile'); }}
            sx={{
              backgroundColor: '#1E3A8A',
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#1E40AF'
              }
            }}
          >
            Update Profile
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Pricing;
